import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GraduationCap, MapPin, Phone, Mail, ArrowRight, Sparkles } from "lucide-react";

const HERO = "https://images.pexels.com/photos/8926848/pexels-photo-8926848.jpeg";

export default function Landing({ embedded = false }) {
  const [news, setNews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/news").then(r => setNews(r.data)).catch(()=>{});
    api.get("/gallery").then(r => setGallery(r.data)).catch(()=>{});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry submitted! We'll be in touch.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch { toast.error("Failed to submit"); }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen">
      {!embedded && (
      <nav className="border-b-2 border-black bg-[#FBF9F6] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-10 h-10 bg-[#C4B5FD] border-2 border-black rounded-lg grid place-items-center shadow-[3px_3px_0_0_#000]">
              <GraduationCap size={22} strokeWidth={2.5} />
            </div>
            <span className="display text-2xl font-bold">Ponderr Schoolz</span>
          </Link>
          <div className="flex gap-3">
            <Link to="/parent-access" className="nb-btn nb-btn-tertiary" data-testid="parent-access-link">Parent Portal</Link>
            <Link to="/login" className="nb-btn" data-testid="login-link">Sign In <ArrowRight size={16}/></Link>
          </div>
        </div>
      </nav>
      )}

      {/* Hero - asymmetric */}
      <section className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7 space-y-6">
          <div className="nb-badge inline-block">EST. 1998 • CBSE & MICROSOFT EDUCATION TENANT</div>
          <h1 className="display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05]">
            A school where <span className="bg-[#FDE047] px-2 border-2 border-black inline-block -rotate-1">curiosity</span> meets discipline.
          </h1>
          <p className="text-lg max-w-xl text-slate-700">
            One platform for admissions, attendance, marks, fees and parent communication —
            integrated with your Microsoft 365 / Azure AD school tenant.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#enquire" className="nb-btn" data-testid="enquire-now-btn">Enquire Now <Sparkles size={16}/></a>
            <Link to="/login" className="nb-btn nb-btn-ghost" data-testid="hero-signin-btn">Sign In to Dashboard</Link>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="nb-card p-3 -rotate-2 hover:rotate-0 transition-transform">
            <img src={HERO} alt="Students" className="w-full h-80 object-cover rounded-md border-2 border-black"/>
          </div>
        </div>
      </section>

      {/* News tiles bento */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <h2 className="display text-3xl font-bold mb-6">School Updates</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {news.length === 0 && [1,2,3].map(i =>
            <div key={i} className="nb-card p-6 h-40 bg-[#86EFAC]">
              <div className="nb-label">No updates</div>
              <p className="mt-2">Check back soon for announcements.</p>
            </div>
          )}
          {news.map((n, i) => (
            <div key={n.news_id} className={`nb-card p-6 ${["bg-[#FDE047]","bg-[#86EFAC]","bg-[#C4B5FD]","bg-[#93C5FD]"][i%4]}`} data-testid={`news-${n.news_id}`}>
              <div className="nb-label">{new Date(n.created_at).toLocaleDateString()}</div>
              <h3 className="display text-xl font-bold mt-2">{n.title}</h3>
              <p className="mt-2 text-sm">{n.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Gallery */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <h2 className="display text-3xl font-bold mb-6">Gallery</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {gallery.map(g =>
            <div key={g.item_id} className="nb-card p-3" data-testid={`gallery-${g.item_id}`}>
              <img src={g.image_url} alt={g.title} className="w-full h-56 object-cover rounded border-2 border-black"/>
              <div className="p-2"><div className="font-bold">{g.title}</div>
                <div className="text-xs text-slate-600">{g.caption}</div></div>
            </div>
          )}
        </div>
      </section>

      {/* Why us */}
      <section className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-4 gap-6">
        {[
          {t:"Microsoft 365", d:"Single sign-on with your Azure AD tenant.", bg:"bg-[#93C5FD]"},
          {t:"Live Attendance", d:"SMS to parents on every absence.", bg:"bg-[#86EFAC]"},
          {t:"Smart Fees", d:"Online & offline. Auto-receipts.", bg:"bg-[#FDE047]"},
          {t:"Parent Portal", d:"OTP-secure access to marks cards.", bg:"bg-[#C4B5FD]"},
        ].map((f,i)=>(
          <div key={f.t} className={`nb-card p-6 ${f.bg}`}>
            <div className="nb-label">FEATURE {i+1}</div>
            <h3 className="display text-xl font-bold mt-1">{f.t}</h3>
            <p className="text-sm mt-2">{f.d}</p>
          </div>
        ))}
      </section>

      {/* Enquiry form */}
      <section id="enquire" className="max-w-7xl mx-auto px-6 py-16">
        <div className="nb-card p-8 bg-[#FDE047]">
          <h2 className="display text-3xl font-bold mb-2">Admissions & Enquiries</h2>
          <p className="text-slate-700 mb-6">Drop us a note — our admissions team will get back within 24 hours.</p>
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
            <input data-testid="enq-name" className="nb-input" placeholder="Full Name" required
                   value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/>
            <input data-testid="enq-email" className="nb-input" placeholder="Email" type="email" required
                   value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/>
            <input data-testid="enq-phone" className="nb-input md:col-span-2" placeholder="Phone" required
                   value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})}/>
            <textarea data-testid="enq-message" className="nb-textarea md:col-span-2" placeholder="How can we help?" rows={4} required
                      value={form.message} onChange={e=>setForm({...form, message:e.target.value})}/>
            <button data-testid="enq-submit" disabled={submitting} className="nb-btn md:col-span-2 justify-center">
              {submitting? "Submitting..." : "Send Enquiry"} <ArrowRight size={16}/>
            </button>
          </form>
        </div>
      </section>

      {!embedded && (
      <footer className="border-t-2 border-black bg-white mt-10">
        <div className="max-w-7xl mx-auto px-6 py-8 grid md:grid-cols-3 gap-6">
          <div>
            <div className="display text-xl font-bold">Ponderr Schoolz</div>
            <p className="text-sm text-slate-600 mt-2">Shaping curious, kind humans since 1998.</p>
            <div className="flex gap-3 mt-3">
              <a href="https://facebook.com/ponderr" target="_blank" rel="noreferrer" className="nb-btn nb-btn-ghost px-3 py-1" data-testid="footer-facebook">Facebook</a>
              <a href="https://instagram.com/ponderr" target="_blank" rel="noreferrer" className="nb-btn nb-btn-ghost px-3 py-1" data-testid="footer-instagram">Instagram</a>
              <a href="https://youtube.com/@ponderr" target="_blank" rel="noreferrer" className="nb-btn nb-btn-ghost px-3 py-1" data-testid="footer-youtube">YouTube</a>
            </div>
          </div>
          <div className="text-sm space-y-1">
            <div className="flex items-center gap-2"><MapPin size={14}/> Alphabudzz, Karnataka, India</div>
            <div className="flex items-center gap-2"><Phone size={14}/> +91 740 600 6666</div>
            <div className="flex items-center gap-2"><Mail size={14}/> support@ponderr.school</div>
          </div>
          <div className="text-xs text-slate-500">© {new Date().getFullYear()} Ponderr Schoolz.</div>
        </div>
      </footer>
      )}
    </div>
  );
}
